import pino from 'pino';

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal' | 'silent';

/* eslint-disable @typescript-eslint/no-explicit-any */
const getLogLevel = (): LogLevel => {
  try {
    return ((import.meta as any).env?.VITE_LOG_LEVEL as LogLevel) ?? 'debug';
  } catch {
    return 'debug';
  }
};
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Root pino instance configured for the browser.
 *
 * - Outputs via the browser's `console.*` methods.
 * - Log level controlled via `VITE_LOG_LEVEL` env var (default: `debug`).
 * - All namespaces enabled by default — filter by level, not by namespace.
 *
 * Levels (lowest → highest): trace, debug, info, warn, error, fatal, silent.
 */
const rootLogger = pino({
  level: getLogLevel(),
  browser: {
    asObject: false,
  },
});

/**
 * Namespace-based logger backed by pino child loggers.
 *
 * Every log entry includes the namespace so it's easy to filter in the
 * browser console. API mirrors the backend Logger.
 *
 * @example
 * const logger = new Logger('MyService');
 * logger.info('started');
 * logger.info(`item count: ${count}`);
 * logger.warn('slow query', { ms: 320 });
 * logger.error('failed', error);
 */
export class Logger {
  private child: pino.Logger;

  constructor(namespace: string) {
    this.child = rootLogger.child({ namespace });
  }

  /** Debug-level message. */
  debug(message: string, ...args: unknown[]) {
    this.log('debug', message, args);
  }

  /** Info-level message. */
  info(message: string, ...args: unknown[]) {
    this.log('info', message, args);
  }

  /** Warning-level message. */
  warn(message: string, ...args: unknown[]) {
    this.log('warn', message, args);
  }

  /** Error-level message. */
  error(message: string, ...args: unknown[]) {
    this.log('error', message, args);
  }

  /** Fatal-level message. */
  fatal(message: string, ...args: unknown[]) {
    this.log('fatal', message, args);
  }

  /**
   * Internal dispatch.
   *
   * - If the first arg is an Error, pass it as `{ err }` merge object (pino serialises it).
   * - If the first arg is a plain object, merge it into the log entry.
   * - Otherwise pass all args packed as `{ args }`.
   */
  private log(level: pino.Level, message: string, args: unknown[]) {
    if (args.length === 0) {
      this.child[level](message);
      return;
    }

    if (args.length === 1) {
      const first = args[0];
      if (first instanceof Error) {
        this.child[level]({ err: first }, message);
        return;
      }
      if (typeof first === 'object' && first !== null && !Array.isArray(first)) {
        this.child[level](first as Record<string, unknown>, message);
        return;
      }
    }

    this.child[level]({ args }, message);
  }
}
